import Chapter from "./Chapter";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Flame, Truck, Wrench, Store, Forklift, ArrowUpRight } from "lucide-react";
import { ASSETS } from "./assets";

const MotionLink = motion(Link);

const CASES = [
  {
    icon: Flame,
    title: "Sapeurs-pompiers & secours",
    desc: "Maîtrisez rapidement un feu de véhicule, refroidissez les batteries lithium des VE et extrayez l'épave de la zone à risque.",
    img: ASSETS.fireSuppression,
    tag: "Incendie",
    to: "/sapeurs-pompiers",
  },
  {
    icon: Truck,
    title: "Fourrière & dépannage",
    desc: "Enlevez un véhicule en panne, mal garé ou inaccessible — sans le démarrer ni risquer de l'endommager.",
    img: ASSETS.carFire,
    tag: "Enlèvement",
    to: "/fourriere-depannage",
  },
  {
    icon: Wrench,
    title: "Garages & carrosseries",
    desc: "Déplacez les véhicules dans l'atelier sans clé ni moteur, optimisez l'espace et gagnez un temps précieux.",
    img: ASSETS.underVehicle,
    tag: "Atelier",
    to: "/garages-concessions",
  },
  {
    icon: Store,
    title: "Concessions & showrooms",
    desc: "Positionnez vos véhicules d'exposition au centimètre près, sans rayures ni manœuvres compliquées.",
    img: ASSETS.product,
    tag: "Exposition",
    contain: true,
    to: "/garages-concessions",
  },
  {
    icon: Forklift,
    title: "Logistique industrielle & charges délicates",
    desc: "Déplacez machines, équipements fragiles et charges lourdes jusqu'à 3 000 kg, au centimètre près, sans chariot élévateur.",
    img: ASSETS.logistique,
    tag: "Manutention",
    to: "/logistique-industrielle",
    wide: true,
  },
];

export default function UseCases() {
  return (
    <section id="cas-usage" data-testid="usecases-section" className="py-24 lg:py-32 bg-[#FAFAFA]">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <Chapter index={4} label="Cas d'usage" />
          <h2 className="font-display mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Partout où il faut bouger une charge lourde.
          </h2>
          <p className="mt-4 text-lg text-[#52525B]">
            Du parking de la caserne au hall logistique, Sapeur-360 s'adapte à chaque métier qui
            déplace des véhicules ou des charges délicates au quotidien.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {CASES.map((c, i) => (
            <MotionLink
              key={c.title}
              to={c.to}
              data-testid={`usecase-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`group flex flex-col bg-white border border-zinc-200 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${c.wide ? "md:col-span-2" : ""}`}
            >
              <div className={`relative bg-black overflow-hidden aspect-[16/9] ${c.wide ? "md:aspect-[32/9]" : ""}`}>
                <img
                  src={c.img}
                  alt={c.title}
                  className={`absolute inset-0 h-full w-full ${c.contain ? "object-contain p-3" : "object-cover"} transition-transform duration-500 group-hover:scale-105`}
                />
                <span className="absolute top-3 left-3 font-mono-tech text-[11px] uppercase tracking-wider text-white bg-black/70 border border-white/10 px-2.5 py-1 rounded-sm">
                  {c.tag}
                </span>
                {c.wide && (
                  <span className="absolute top-3 right-3 font-mono-tech text-[11px] uppercase tracking-wider text-[#0A0A0A] bg-white/95 px-2.5 py-1 rounded-sm">
                    Nouveau
                  </span>
                )}
              </div>
              <div className="p-7">
                <div className="flex items-center justify-between">
                  <span className="grid h-11 w-11 place-items-center bg-[#0A0A0A] text-white rounded-sm group-hover:bg-[#E63946] transition-colors duration-300">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-[#A1A1AA] group-hover:text-[#E63946] transition-colors" />
                </div>
                <h3 className="font-display mt-5 text-xl font-bold tracking-tight">{c.title}</h3>
                <p className="mt-2 text-[#52525B] leading-relaxed">{c.desc}</p>
              </div>
            </MotionLink>
          ))}
        </div>
      </div>
    </section>
  );
}
